// src/components/roles/RoleWizard/RoleWizard.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Role, Permission } from '../../../types/role.types';
import { useRoleWizard } from './RoleWizard.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { RolesServiceHelpers } from '../../../utils/api-helpers/RolesService.helpers';
import { convertPermissionsFromAPI } from '../../../utils/permissions.utils';
import ProgressBar from '../../inventory/add-panel/ProgressBar';
import StepIndicators from '../../inventory/add-panel/StepIndicators';
import type { StepConfig } from '../../inventory/add-panel/StepIndicators';
import WizardStep1 from './WizardStep1';
import WizardStep2 from './WizardStep2';
import WizardStep3 from './WizardStep3';
import { usePermissionCatalog } from '../../../hooks/usePermissionCatalog';
import {
  StyledDrawer,
  DrawerContainer,
  DrawerHeader,
  DrawerTitle,
  CloseButton,
  DrawerContent,
  DrawerFooter,
  CancelButton,
  NextButton,
  BackButton,
  SaveButton,
} from './RoleWizard.styles';

export interface RoleWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roleData: { name: string; description?: string; permissions?: any[] }) => Promise<void>;
  editRole?: Role | null;
}

// Configuración de pasos con iconos y colores
const roleSteps: StepConfig[] = [
  {
    id: 1,
    title: 'Información Básica',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    ),
    color: {
      active: 'bg-purple-600',
      completed: 'bg-purple-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-purple-800',
      inactive: 'text-gray-500',
    },
  },
  {
    id: 2,
    title: 'Permisos',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
        <path d="M9 9h6v6H9z"></path>
        <path d="M9 1v6M15 1v6M9 15v8M15 15v8M1 9h6M1 15h6M17 9h6M17 15h6"></path>
      </svg>
    ),
    color: {
      active: 'bg-blue-600',
      completed: 'bg-blue-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-blue-800',
      inactive: 'text-gray-500',
    },
  },
  {
    id: 3,
    title: 'Confirmación',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    ),
    color: {
      active: 'bg-emerald-500',
      completed: 'bg-emerald-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-emerald-800',
      inactive: 'text-gray-500',
    },
  },
];

const RoleWizard: React.FC<RoleWizardProps> = ({ isOpen, onClose, onSave, editRole = null }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [permissionsLoadError, setPermissionsLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});
  const [loadedPermissions, setLoadedPermissions] = useState<Permission[] | null>(null);
  const { structure: permissionStructure, loading: loadingCatalog } = usePermissionCatalog();

  const loadPermissions = useCallback(() => {
    if (!editRole?.id) return;

    setIsLoadingPermissions(true);
    setPermissionsLoadError(null);
    setLoadedPermissions(null);

    RolesServiceHelpers.rolesControllerGetPermissions(editRole.id)
      .then((response) => {
        const resp: any = response;
        let permissionsData: Permission[] = [];

        if (Array.isArray(resp)) {
          permissionsData = resp;
        } else if (Array.isArray(resp?.data)) {
          permissionsData = resp.data;
        } else if (Array.isArray(resp?.permissions)) {
          permissionsData = resp.permissions;
        } else if (Array.isArray(resp?.data?.permissions)) {
          permissionsData = resp.data.permissions;
        }

        setLoadedPermissions(permissionsData);
        setPermissionsLoadError(null);
      })
      .catch((error) => {
        console.error('Error loading permissions:', error);
        // Do NOT fall back to [] — that would wipe permissions on save
        setLoadedPermissions(null);
        setPermissionsLoadError(
          'No se pudieron cargar los permisos del rol. Reintente antes de guardar.',
        );
      })
      .finally(() => {
        setIsLoadingPermissions(false);
      });
  }, [editRole?.id]);

  // Cargar permisos cuando se abre en modo edición
  useEffect(() => {
    if (isOpen && editRole?.id) {
      loadPermissions();
    } else if (!isOpen) {
      setLoadedPermissions(null);
      setPermissionsLoadError(null);
    }
  }, [isOpen, editRole?.id, loadPermissions]);

  // Convertir permisos cargados al formato wizard
  // Usar !== null para distinguir entre "aún no cargados" (null) y "cargados pero vacíos" ([])
  const wizardPermissions = useMemo(
    () => (loadedPermissions !== null ? convertPermissionsFromAPI(loadedPermissions) : undefined),
    [loadedPermissions],
  );

  // Usar useMemo para que React detecte cambios en wizardPermissions
  // PERO solo cuando realmente cambian los permisos, no cuando se cargan por primera vez
  const initialData = useMemo(() => {
    if (editRole) {
      return {
        name: editRole.name,
        description: editRole.description,
        permissions: wizardPermissions, // Usar permisos convertidos
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRole?.id, editRole?.name, editRole?.description, wizardPermissions]); // Usar editRole?.id en lugar de editRole completo

  const wizard = useRoleWizard({
    initialData,
    onSave: async (data) => {
      setIsSaving(true);
      try {
        // La conversión de permisos se hace en el hook useRoleWizard
        // Solo pasamos los datos al onSave del padre
        await onSave({
          name: data.name,
          description: data.description,
          permissions: data.permissions as any, // Los permisos ya vienen convertidos del hook
        });
        onClose();
      } catch (error) {
        console.error('Error saving role:', error);
      } finally {
        setIsSaving(false);
      }
    },
    onClose,
    isOpen,
  });

  // Interceptar ESC para mostrar confirmación de cierre
  useEscapeKey(
    () => {
      if (isOpen && !isSaving && !wizard.showCancelConfirmation) {
        wizard.handleCloseClick();
      }
    },
    isOpen && !isSaving && !wizard.showCancelConfirmation,
  );

  const handleNext = () => {
    // Validate step 1
    if (wizard.currentStep === 1) {
      const name = wizard.formData.name.trim();
      if (name.length < 3) {
        setErrors({ name: 'El nombre debe tener al menos 3 caracteres' });
        return;
      }
      if (name.length > 50) {
        setErrors({ name: 'El nombre no puede tener más de 50 caracteres' });
        return;
      }
      if (name.startsWith(' ') || name.endsWith(' ')) {
        setErrors({ name: 'El nombre no puede empezar o terminar con espacios' });
        return;
      }
      setErrors({});
    }
    wizard.nextStep();
  };

  const handleBack = () => {
    wizard.prevStep();
  };

  const handleSave = async () => {
    if (editRole && (permissionsLoadError || loadedPermissions === null)) {
      return;
    }
    await wizard.handleSave();
  };

  const canSave =
    !isSaving &&
    !isLoadingPermissions &&
    !permissionsLoadError &&
    (!editRole || loadedPermissions !== null);

  const renderStepContent = () => {
    switch (wizard.currentStep) {
      case 1:
        return (
          <WizardStep1
            formData={wizard.formData}
            setFormData={wizard.setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <WizardStep2 formData={wizard.formData} wizard={wizard} structure={permissionStructure} />
        );
      case 3:
        return <WizardStep3 formData={wizard.formData} />;
      default:
        return null;
    }
  };

  return (
    <>
      <StyledDrawer
        anchor="right"
        open={isOpen}
        onClose={(event, reason) => {
          // Solo permitir cerrar con ESC, no con click en el backdrop
          // El botón X llama directamente a handleCloseClick
          if (reason === 'escapeKeyDown') {
            wizard.handleCloseClick();
          }
          // Si es backdropClick, no hacer nada - el drawer NO se cierra
        }}
        ModalProps={{
          onBackdropClick: (event) => {
            // Prevenir el cierre al hacer click fuera del drawer
            event.stopPropagation();
          },
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 32px 0 rgba(16,30,54,0.08)',
          },
        }}
      >
        <DrawerContainer>
          {/* Header */}
          <DrawerHeader>
            <DrawerTitle>{editRole ? 'Editar Rol' : 'Crear Nuevo Rol'}</DrawerTitle>
            <CloseButton onClick={wizard.handleCloseClick}>
              <CloseIcon />
            </CloseButton>
          </DrawerHeader>

          {/* Progress Bar and Step Indicators */}
          <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ProgressBar currentStep={wizard.currentStep} totalSteps={3} />
            <StepIndicators currentStep={wizard.currentStep} totalSteps={3} steps={roleSteps} />
          </Box>

          {/* Content */}
          <DrawerContent>
            {isLoadingPermissions ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  minHeight: 200,
                }}
              >
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                  Cargando permisos...
                </Typography>
              </Box>
            ) : permissionsLoadError ? (
              <Box sx={{ p: 3 }}>
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={loadPermissions}>
                      Reintentar
                    </Button>
                  }
                >
                  {permissionsLoadError}
                </Alert>
              </Box>
            ) : loadingCatalog ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  minHeight: 200,
                }}
              >
                <CircularProgress size={36} />
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                  Cargando catálogo de permisos...
                </Typography>
              </Box>
            ) : (
              renderStepContent()
            )}
          </DrawerContent>

          {/* Footer */}
          <DrawerFooter>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {wizard.currentStep > 1 && (
                <BackButton onClick={handleBack} disabled={isSaving || !!permissionsLoadError}>
                  ← Atrás
                </BackButton>
              )}
              <CancelButton onClick={wizard.handleCloseClick} disabled={isSaving}>
                Cancelar
              </CancelButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {wizard.currentStep < 3 ? (
                <NextButton
                  onClick={handleNext}
                  disabled={!wizard.canGoNext || isSaving || !!permissionsLoadError}
                >
                  Siguiente →
                </NextButton>
              ) : (
                <SaveButton
                  onClick={handleSave}
                  disabled={!canSave}
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isSaving ? 'Guardando...' : editRole ? 'Actualizar Rol' : 'Crear Rol'}
                </SaveButton>
              )}
            </Box>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>

      {/* Modal de confirmación de cierre */}
      <CloseConfirmationModal
        open={wizard.showCancelConfirmation}
        onClose={() => wizard.setShowCancelConfirmation(false)}
        onConfirm={wizard.cancelOperation}
      />
    </>
  );
};

export default RoleWizard;
